import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import MoreContentRequester from 'components/MoreContentRequester';
import Button from 'components/shared/Button';
import { useExtensionPresence } from 'hooks/useExtensionPresence';
import { usePortalName } from 'hooks/usePortalConnections';
import PortalConnection from 'models/PortalConnection';
import { portalConnectionQueryKeys } from 'services/api/portalConnections';
import { startPortalScans } from 'services/api/portalScans';
import { workflowQueryKeys } from 'services/api/workflows';
import { useToastActions } from 'stores/toastStore';
import { Body, BodySmall } from 'styles/typography';

import ScanCard from './ScanCard';
import { orderScanCards, reportScanResults, ScanCard as ScanCardModel, scanCard } from './scanCards';
import ScanResultsSlideout from './ScanResultsSlideout';
import StartScanModal from './StartScanModal';
import useScanRunner, { outstandingDiscovery } from './useScanRunner';
import useScans from './useScans';

interface Props {
  connections: PortalConnection[];
  // Opens a task's slideout — owned by the page, which holds the one slideout both tabs use.
  onOpenTask: (workflowId: string, siblingWorkflowIds: string[]) => void;
}

/**
 * The Portal Scans tab: one card per `portal_discovery` run, newest first, plus the multi-select
 * that starts new ones.
 *
 * The page shell around this — header, tab bar, active-scan banner — belongs to TasksPage. This
 * owns the list, its results slideout, and the start gesture.
 */
export default function PortalScansTab({ connections, onOpenTask }: Props) {
  const portalName = usePortalName();
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const { workflows, isLoading, isError, query } = useScans();
  const { runScan, runScans, active } = useScanRunner();
  const { present } = useExtensionPresence();

  const [modalOpen, setModalOpen] = useState(false);
  const [openResults, setOpenResults] = useState<ScanCardModel | null>(null);

  const cards = useMemo(
    () => orderScanCards(workflows.map((workflow) => scanCard(workflow, { portalName }))),
    [workflows, portalName]
  );

  // Runs whose sweep has not happened yet: created, waiting on this browser, and nobody driving
  // them. A reload mid-sweep lands here, as does a sweep that failed — the run stays open, so the
  // card offers to pick it up rather than stranding it.
  const awaitingBrowser = useMemo(
    () => new Set(workflows.filter((workflow) => outstandingDiscovery(workflow)).map((workflow) => workflow.id)),
    [workflows]
  );

  // Which portals sweep in THIS browser. Headless runs are picked up by a worker; extension and
  // manual clients have no worker, so the page drives the sweep itself or nothing does.
  const drivenHere = useMemo(
    () =>
      new Set(connections.filter((connection) => !connection.isHeadless).map((c) => c.sourceSystem)),
    [connections]
  );

  const start = useMutation({
    mutationFn: (sourceSystems: string[]) => startPortalScans(sourceSystems),
    onSuccess: async (results) => {
      setModalOpen(false);
      reportScanResults(results, portalName).forEach((text) => addToast({ text }));
      // Both lists move: the scan list gains rows, and the connection rows gain the
      // `liveScanWorkflowId` the page's banner is keyed on.
      queryClient.invalidateQueries({ queryKey: workflowQueryKeys.lists });
      queryClient.invalidateQueries({ queryKey: portalConnectionQueryKeys.list() });

      // Creating the run is not running it. In extension mode the sweep happens here, so the
      // runs this start just created are driven straight away — otherwise Start Scan would leave
      // them sitting in `action_required` while the banner claimed a scan was under way.
      const toDrive = results.filter(
        (result) => result.created && result.workflowId && drivenHere.has(result.sourceSystem)
      );
      if (toDrive.length > 0) await runScans(toDrive.map((result) => result.workflowId!));
    },
    onError: () => addToast({ text: 'We couldn’t start the scan. Try again in a moment.' }),
  });

  return (
    <Wrapper>
      <Toolbar>
        <Button size='small' onClick={() => setModalOpen(true)} data-testid='start-scan'>
          Start a Portal Scan
        </Button>
      </Toolbar>

      {isError ? (
        <Message data-testid='scans-error'>We couldn’t load your portal scans.</Message>
      ) : isLoading ? (
        <Message data-testid='scans-loading'>Loading portal scans…</Message>
      ) : cards.length === 0 ? (
        <Message data-testid='scans-empty'>
          <Body>No portal scans yet.</Body>
          <BodySmall>Start one to check your portals for new referral activity.</BodySmall>
        </Message>
      ) : (
        <Cards data-testid='scan-cards'>
          {cards.map((card) => (
            <ScanCard
              key={card.id}
              card={card}
              onOpenResults={setOpenResults}
              // Only offered where this browser is the thing that sweeps, and only once the
              // extension has actually answered — headless runs belong to a worker, and without
              // the extension there is nothing here to run them with.
              onRunInBrowser={
                present === true && drivenHere.has(card.sourceSystem) && awaitingBrowser.has(card.workflowId)
                  ? () => runScan(card.workflowId)
                  : undefined
              }
              runStatus={active?.workflowId === card.workflowId ? active.status : undefined}
            />
          ))}
          <MoreContentRequester resource={query} />
        </Cards>
      )}

      {modalOpen && (
        <StartScanModal
          connections={connections}
          submitting={start.isPending}
          onCancel={() => setModalOpen(false)}
          onStart={(sourceSystems) => start.mutate(sourceSystems)}
        />
      )}

      <ScanResultsSlideout
        card={openResults}
        onClose={() => setOpenResults(null)}
        onOpenTask={(workflowId, siblings) => {
          setOpenResults(null);
          onOpenTask(workflowId, siblings);
        }}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 48px 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.black75};
`;
